import { usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { MapPin } from "lucide-react"
import type { PageProps } from "@/types/inertia"

export type BaseLocation = {
  slug: string
  name: string
  region: string | null
  address: string | null
  maps_url: string | null
  // Paths on the Sanctuary site for this location's page and its gallery.
  path: string
  // Her own page for this town, on her site.
  town_path: string
  gallery_path: string
  photo_url: string | null
}

export type BaseBlockData = {
  content: { heading?: string; body?: string; _hidden?: boolean }
  photo: { url: string | null; alt_text: string | null } | null
  locations: BaseLocation[]
}

// "Her permanent base" — the venue-as-credential block on Gisela's pages
// (docs/two-brand-split-strategy.md §13.1). Links across to the Sanctuary
// site; the cross-domain root comes from the shared brand_urls prop so no
// component ever hardcodes the other host.
export function SanctuaryBaseBlock({ base, className = "bg-stone" }: { base: BaseBlockData; className?: string }) {
  const { props } = usePage<PageProps>()
  const { brand_urls } = props
  const t = props.translations.gisela_pages.base

  return (
    <section className={`${className} py-16`}>
      <div className="mx-auto max-w-5xl px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-8 rounded-2xl bg-white p-6 ring-1 ring-hairline md:grid-cols-2 lg:p-10">
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-widest text-ink-muted">
              {t.eyebrow}
            </p>
            <h2>{base.content.heading || t.heading}</h2>
            {base.content.body && (
              <div className="body-content mt-4 text-ink-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{base.content.body}</ReactMarkdown>
              </div>
            )}
            {base.locations.map((location) => (
              <p key={location.path} className="mt-4 flex items-start gap-2 text-sm text-ink-muted">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <a
                    href={`${brand_urls.sanctuary}${location.path}`}
                    className="text-sm font-medium"
                  >
                    Sanctuary Therapy Centre, {location.name}
                  </a>
                  {location.address && (
                    <>
                      <br />
                      {location.address}
                      {location.maps_url && (
                        <>
                          {" · "}
                          <a href={location.maps_url} target="_blank" rel="noopener">
                            {t.map}
                          </a>
                        </>
                      )}
                    </>
                  )}
                </span>
              </p>
            ))}
          </div>
          <div>
            {base.photo?.url ? (
              <img
                src={base.photo.url}
                alt={base.photo.alt_text ?? "Sanctuary Therapy Centre"}
                className="aspect-[4/3] w-full rounded-xl object-cover"
              />
            ) : (
              <div className="flex aspect-[4/3] w-full items-center justify-center rounded-xl bg-surface text-sm text-ink-muted">
                Sanctuary Therapy Centre
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
